import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Paper,
    TablePagination,
    Tabs,
    Tab,
    Box,
} from '@material-ui/core';
import i18n from 'i18n-js';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PrintIcon from '@material-ui/icons/Print';
import clsx from 'clsx';

import { pagingOptions } from 'constants/arrays';
import { useStyles } from './styles';
import globalStore from 'store/globalStore';
import agent from 'api/agent';
import { PaymentStatus } from 'constants/enums';
import { useAppSelector } from 'index';
import { formatDateIntl } from 'helpers/datetimeHelper';
import { RefundRow } from 'api/models';

enum PaymentType {
    PAYMENT = 'Payment',
    REFUND = 'Refund',
}

interface Transaction {
    id: string;
    paymentDate: number;
    status: PaymentStatus;
    totalAmount: number;
    paymentMethod: string;
    type: PaymentType;
}

export default function Transactions() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const language = useAppSelector((state) => state.global.language);
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);

    const [activeTab, setActiveTab] = useState(0);

    // Payments state
    const [count, setCount] = useState(0);
    const [payments, setPayments] = useState<Transaction[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);

    /* // Refunds state
    const [refundsCount, setRefundsCount] = useState(0);
    const [refunds, setRefunds] = useState<RefundRow[]>([]);
    const [refundsPage, setRefundsPage] = useState(0);
    const [refundsRowsPerPage, setRefundsRowsPerPage] = useState(pagingOptions[0]); */

    function prepareStatus(status: PaymentStatus) {
        switch (status) {
            case PaymentStatus.Open:
                return 'Open';
            case PaymentStatus.Success:
                return 'Success';
            case PaymentStatus.Cancelled:
                return 'Cancelled';
            case PaymentStatus.Failed:
                return 'Failed';
            default:
                return 'Failed';
        }
    }

    function preparePaymentMethod(method: string) {
        if (method.toUpperCase() === 'CARD') {
            return i18n.t('orders.card');
        } else if (method.toUpperCase() === 'CASH') {
            return i18n.t('orders.cash');
        } else {
            return method;
        }
    }

    async function downloadReceiptPDF(id: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GeneratePaymentReceiptPDF(id);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    async function printReceiptPDF(id: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GeneratePaymentReceiptPDFForPrint(id);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    async function downloadRefundReceiptPDF(refundReceiptId: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GenerateRefundReceiptPDF(refundReceiptId);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    async function printRefundReceiptPDF(refundReceiptId: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Payments.GenerateRefundReceiptPDFForPrint(refundReceiptId);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    useEffect(() => {
        const getPayments = async () => {
            dispatch(globalStore.actionCreators.showSpiner());
            try {
                const closings = await agent.Payments.GetPayments(page, rowsPerPage);
                if (closings) {
                    setPayments(closings.list);
                    setCount(closings.count);
                }
            } catch (error) {
                dispatch(globalStore.actionCreators.showToaster('error', error));
            } finally {
                dispatch(globalStore.actionCreators.hideSpiner());
            }
        };
        getPayments();
    }, [page, rowsPerPage]);

    /* useEffect(() => {
        if (activeTab !== 1) {
            return;
        }
        const getRefunds = async () => {
            dispatch(globalStore.actionCreators.showSpiner());
            try {
                const result = await agent.Payments.GetRefunds(refundsPage, refundsRowsPerPage);
                if (result) {
                    setRefunds(result.list);
                    setRefundsCount(result.count);
                }
            } catch (error) {
                dispatch(globalStore.actionCreators.showToaster('error', error));
            } finally {
                dispatch(globalStore.actionCreators.hideSpiner());
            }
        };
        getRefunds();
    }, [activeTab, refundsPage, refundsRowsPerPage]); */

    return (
        <Box className={classes.root}>
            <Paper>
                <Toolbar
                    style={{
                        paddingBottom: '10px',
                        paddingTop: '10px',
                        justifyContent: 'flex-start',
                        marginTop: 0,
                    }}
                >
                    <Box style={{ display: 'flex', flexDirection: 'row' }}>
                        <IconButton
                            aria-label="delete"
                            size="small"
                            className={classes.iconButton}
                            onClick={() => history.goBack()}
                        >
                            <ArrowBackIcon fontSize="inherit" />
                        </IconButton>
                    </Box>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newValue: number) => setActiveTab(newValue)}
                        indicatorColor="primary"
                        textColor="primary"
                        style={{ borderBottom: '1px solid #e0e0e0' }}
                    >
                        <Tab label={i18n.t('transactions.payments')} />
                        {/* <Tab label={i18n.t('transactions.refundings')} /> */}
                    </Tabs>
                </Toolbar>

                {activeTab === 0 && (
                    <>
                        <TableContainer id="container" style={{ maxHeight: '80%' }}>
                            <Table stickyHeader className={classes.root} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'17%'}
                                            style={{ paddingLeft: 22 }}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.date')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'17%'}
                                            style={{ paddingLeft: 22 }}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.type')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'17%'}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.status')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'15%'}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.amount')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'15%'}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.method')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'15%'}
                                        >
                                            <div
                                                className={classes.tableCellHeaderContainer}
                                                style={{ padding: 16 }}
                                            >
                                                {i18n.t('common.download')}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.map((payment, i) => (
                                        <TableRow
                                            key={i}
                                            className={clsx({
                                                [classes.refundRow]:
                                                    payment.type === PaymentType.REFUND,
                                            })}
                                        >
                                            <TableCell>
                                                <span>
                                                    {formatDateIntl(payment.paymentDate, language)}
                                                </span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>{payment.type}</span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>{prepareStatus(payment.status)}</span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>
                                                    {payment.type === PaymentType.REFUND
                                                        ? '- '
                                                        : ''}
                                                    {payment.totalAmount}{' '}
                                                    {restaurant.paymentDetails.currency || 'CHF'}
                                                </span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>
                                                    {preparePaymentMethod(payment.paymentMethod)}
                                                </span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <IconButton
                                                    disabled={
                                                        payment.status !== PaymentStatus.Success
                                                    }
                                                    aria-label="delete"
                                                    size="small"
                                                    style={{
                                                        marginLeft: 10,
                                                        opacity:
                                                            payment.status === PaymentStatus.Success
                                                                ? 1
                                                                : 0.5,
                                                    }}
                                                    className={classes.iconButton}
                                                    onClick={() =>
                                                        payment.status === PaymentStatus.Success &&
                                                        downloadReceiptPDF(payment.id)
                                                    }
                                                >
                                                    <PictureAsPdfIcon
                                                        fontSize="medium"
                                                        color="primary"
                                                    />
                                                </IconButton>
                                                <IconButton
                                                    disabled={
                                                        payment.status !== PaymentStatus.Success
                                                    }
                                                    aria-label="delete"
                                                    size="small"
                                                    style={{
                                                        marginLeft: 10,
                                                        opacity:
                                                            payment.status === PaymentStatus.Success
                                                                ? 1
                                                                : 0.5,
                                                    }}
                                                    className={classes.iconButton}
                                                    onClick={() =>
                                                        payment.status === PaymentStatus.Success &&
                                                        printReceiptPDF(payment.id)
                                                    }
                                                >
                                                    <PrintIcon fontSize="medium" color="primary" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            style={{ overflow: 'auto' }}
                            rowsPerPageOptions={pagingOptions}
                            component="div"
                            count={count}
                            rowsPerPage={rowsPerPage}
                            labelRowsPerPage={i18n.t('common.rowsPerPage')}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => setRowsPerPage(+event.target.value)}
                        />
                    </>
                )}

                {/* {activeTab === 1 && (
                    <>
                        <TableContainer id="refunds-container" style={{ maxHeight: '90%' }}>
                            <Table
                                stickyHeader
                                className={classes.root}
                                aria-label="refundings table"
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'18%'}
                                            style={{ paddingLeft: 22 }}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.date')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'14%'}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('transactions.refundNo')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'18%'}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('transactions.originalInvoice')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'14%'}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.amount')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'14%'}
                                        >
                                            <div className={classes.tableCellHeaderContainer}>
                                                {i18n.t('common.method')}
                                            </div>
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableCellHeader}
                                            width={'18%'}
                                        >
                                            <div
                                                className={classes.tableCellHeaderContainer}
                                                style={{ padding: 16 }}
                                            >
                                                {i18n.t('common.download')}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {refunds.map((row, i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                <span>
                                                    {formatDateIntl(row.refundDate, language)}
                                                </span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>{row.refundNumber}</span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>{row.originalTransactionNumber}</span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>-{row.refundedAmount.toFixed(2)} CHF</span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <span>
                                                    {preparePaymentMethod(row.paymentMethod)}
                                                </span>
                                            </TableCell>
                                            <TableCell align={'left'}>
                                                <IconButton
                                                    aria-label="download refund receipt"
                                                    size="small"
                                                    style={{ marginLeft: 10 }}
                                                    className={classes.iconButton}
                                                    onClick={() =>
                                                        downloadRefundReceiptPDF(
                                                            row.refundReceiptId
                                                        )
                                                    }
                                                >
                                                    <PictureAsPdfIcon
                                                        fontSize="medium"
                                                        color="primary"
                                                    />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="print refund receipt"
                                                    size="small"
                                                    style={{ marginLeft: 10 }}
                                                    className={classes.iconButton}
                                                    onClick={() =>
                                                        printRefundReceiptPDF(row.refundReceiptId)
                                                    }
                                                >
                                                    <PrintIcon fontSize="medium" color="primary" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={pagingOptions}
                            component="div"
                            count={refundsCount}
                            rowsPerPage={refundsRowsPerPage}
                            labelRowsPerPage={i18n.t('common.rowsPerPage')}
                            page={refundsPage}
                            onPageChange={(_, newPage) => setRefundsPage(newPage)}
                            onRowsPerPageChange={(event) =>
                                setRefundsRowsPerPage(+event.target.value)
                            }
                        />
                    </>
                )} */}
            </Paper>
        </Box>
    );
}
